/**
 * @fileoverview Root Application Component
 *
 * This is the main entry point for the Seyu application. It sets up:
 * - React Query for server state management
 * - Global auth provider (single source of truth for auth state)
 * - Global providers (DevMode, Tooltips)
 * - Toast notification systems (shadcn Toaster + Sonner)
 * - Client-side routing with React Router
 *
 * @module App
 */

import { Suspense } from "react";
import wLogoIcon from "@/assets/want-w-icon.svg";
import { lazyWithRecovery } from "@/lib/lazyWithRecovery";
import { Capacitor } from "@capacitor/core";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { AnimatedOutlet } from "@/components/layout/AnimatedOutlet";

import { ScrollToTop } from "@/components/layout/ScrollToTop";
import { AttributionHandler } from "@/components/attribution/AttributionHandler";
import { AuthProvider } from "@/contexts/AuthContext";
import { ImpersonationProvider } from "@/contexts/ImpersonationContext";
import { ImpersonationBanner } from "@/components/admin/ImpersonationBanner";
import { DeepLinkProvider } from "@/contexts/DeepLinkContext";
import { DevModeProvider } from "@/contexts/DevModeContext";
import { ExchangeRatesPrefetch } from "@/components/providers/ExchangeRatesPrefetch";
import { CapacitorProvider } from "@/components/providers/CapacitorProvider";
import { EventLoggerProvider } from "@/components/providers/EventLoggerProvider";
import { AdminGuard } from "@/components/admin/AdminGuard";
import { AppAccessGate } from "@/components/app-access/AppAccessGate";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { PhoneFrame } from "@/components/layout/PhoneFrame";
import { GlobalErrorCapture } from "@/components/providers/GlobalErrorCapture";
import { useThemeSync } from "@/hooks/useThemeSync";
import { NewUserOnboardingProvider } from "@/components/providers/NewUserOnboardingProvider";
import { VideoMuteProvider } from "@/contexts/VideoMuteContext";
import { BadgeCelebrationOverlay } from "@/components/badges/BadgeCelebrationOverlay";
import { OtaUpdateNotifier } from "@/components/layout/OtaUpdateNotifier";

/**
 * Component that syncs theme preference with database.
 * Must be inside AuthProvider and ThemeProvider.
 */
function ThemeSyncProvider({ children }: { children: React.ReactNode }) {
  useThemeSync();
  return <>{children}</>;
}
// Eagerly loaded pages (critical path)
import HomePage from "./pages/HomePage";
import SavesPage from "./pages/SavesPage";

// Lazy-loaded pages for code splitting (using shared recovery utility)
const TryOnPage = lazyWithRecovery(() => import("./pages/TryOnPage"));
const ChatPage = lazyWithRecovery(() => import("./pages/ChatPage"));
const NotificationsPage = lazyWithRecovery(() => import("./pages/NotificationsPage"));
const SettingsPage = lazyWithRecovery(() => import("./pages/SettingsPage"));
const SavedItemDetailPage = lazyWithRecovery(() => import("./pages/SavedItemDetailPage"));
const CollectionDetailPage = lazyWithRecovery(() => import("./pages/CollectionDetailPage"));
const ProfilePage = lazyWithRecovery(() => import("./pages/ProfilePage"));
const UserProfilePage = lazyWithRecovery(() => import("./pages/UserProfilePage"));
const SafariExtensionPage = lazyWithRecovery(() => import("./pages/SafariExtensionPage"));
const ChromeExtensionPage = lazyWithRecovery(() => import("./pages/ChromeExtensionPage"));
const CleanOutPage = lazyWithRecovery(() => import("./pages/CleanOutPage"));
const ResetPasswordPage = lazyWithRecovery(() => import("./pages/ResetPasswordPage"));
const PurchasesPage = lazyWithRecovery(() => import("./pages/PurchasesPage"));
// OnboardingPage removed - route redirects to /app/welcome
const WelcomePage = lazyWithRecovery(() => import("./pages/WelcomePage"));
const PriceAlertsPage = lazyWithRecovery(() => import("./pages/PriceAlertsPage"));
const PricingPage = lazyWithRecovery(() => import("./pages/PricingPage"));
// Style Studio pages removed - now integrated into Studio tab via TabView

const ForYouPage = lazyWithRecovery(() => import("./pages/ForYouPage"));
const VoiceAgentPage = lazyWithRecovery(() => import("./pages/VoiceAgentPage"));
const MakeItYoursPage = lazyWithRecovery(() => import("./pages/MakeItYoursPage"));
const PrivacyPolicyPage = lazyWithRecovery(() => import("./pages/PrivacyPolicyPage"));
const AboutPage = lazyWithRecovery(() => import("./pages/AboutPage"));
const NativeAuthCallbackPage = lazyWithRecovery(() => import("./pages/NativeAuthCallbackPage"));
const SharedCollectionPage = lazyWithRecovery(() => import("./pages/SharedCollectionPage"));
const NotFound = lazyWithRecovery(() => import("./pages/NotFound"));
const PriceGuessPage = lazyWithRecovery(() => import("./pages/PriceGuessPage"));
const DailyOutfitPage = lazyWithRecovery(() => import("./pages/DailyOutfitPage"));
const StyleInterviewPage = lazyWithRecovery(() => import("./pages/StyleInterviewPage"));
const LiveTryOnPage = lazyWithRecovery(() => import("./pages/LiveTryOnPage"));
const StylePersonaPage = lazyWithRecovery(() => import("./pages/StylePersonaPage"));
const AccountSecurityPage = lazyWithRecovery(() => import("./pages/AccountSecurityPage"));
const NotificationSettingsPage = lazyWithRecovery(() => import("./pages/NotificationSettingsPage"));
const RegionCurrencyPage = lazyWithRecovery(() => import("./pages/RegionCurrencyPage"));
const ProfileStylePage = lazyWithRecovery(() => import("./pages/ProfileStylePage"));

// Lazy-loaded admin pages for code splitting
const AdminLayout = lazyWithRecovery(() => import("./components/admin/AdminLayout"));
const AdminDashboard = lazyWithRecovery(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazyWithRecovery(() => import("./pages/admin/AdminUsers"));
const AdminUserResults = lazyWithRecovery(() => import("./pages/admin/AdminUserResults"));
const AdminReports = lazyWithRecovery(() => import("./pages/admin/AdminReports"));
const AdminMessaging = lazyWithRecovery(() => import("./pages/admin/AdminMessaging"));
const AdminPromoCards = lazyWithRecovery(() => import("./pages/admin/AdminPromoCards"));
const AdminAnalytics = lazyWithRecovery(() => import("./pages/admin/AdminAnalytics"));

const AdminCohorts = lazyWithRecovery(() => import("./pages/admin/AdminCohorts"));
const AdminFeatureAdoption = lazyWithRecovery(() => import("./pages/admin/AdminFeatureAdoption"));
const AdminGrowth = lazyWithRecovery(() => import("./pages/admin/AdminGrowth"));

const AdminQuickTry = lazyWithRecovery(() => import("./pages/admin/AdminQuickTry"));
const AdminAIPrompts = lazyWithRecovery(() => import("./pages/admin/AdminAIPrompts"));
const AdminAPIUsage = lazyWithRecovery(() => import("./pages/admin/AdminAPIUsage"));
const AdminSponsoredPolls = lazyWithRecovery(() => import("./pages/admin/AdminSponsoredPolls"));
const AdminReferrals = lazyWithRecovery(() => import("./pages/admin/AdminReferrals"));
const AdminFeedback = lazyWithRecovery(() => import("./pages/admin/AdminFeedback"));
const AdminUserPersonas = lazyWithRecovery(() => import("./pages/admin/AdminUserPersonas"));
const AdminWhatsNew = lazyWithRecovery(() => import("./pages/admin/AdminWhatsNew"));
const AdminEvents = lazyWithRecovery(() => import("./pages/admin/AdminEvents"));
const AdminSavedItems = lazyWithRecovery(() => import("./pages/admin/AdminSavedItems"));
const AdminGenerations = lazyWithRecovery(() => import("./pages/admin/AdminGenerations"));
const AdminSocialProof = lazyWithRecovery(() => import("./pages/admin/AdminSocialProof"));
const AdminRoadmapPolls = lazyWithRecovery(() => import("./pages/admin/AdminRoadmapPolls"));
const AdminAppReviews = lazyWithRecovery(() => import("./pages/admin/AdminAppReviews"));
const AdminBlog = lazyWithRecovery(() => import("./pages/admin/AdminBlog"));
const AdminErrorIntelligence = lazyWithRecovery(() => import("./pages/admin/AdminErrorIntelligence"));
const AdminFeatureFlags = lazyWithRecovery(() => import("./pages/admin/AdminFeatureFlags"));
const AdminAppAccess = lazyWithRecovery(() => import("./pages/admin/AdminAppAccess"));
const AdminItemIssues = lazyWithRecovery(() => import("./pages/admin/AdminItemIssues"));
const AdminShareTexts = lazyWithRecovery(() => import("./pages/admin/AdminShareTexts"));
const WhatsNewPage = lazyWithRecovery(() => import("./pages/WhatsNewPage"));
const FriendsPage = lazyWithRecovery(() => import("./pages/FriendsPage"));

// Marketing site (web only, lazy-loaded)
const MarketingLayout = lazyWithRecovery(() => import("./components/marketing/MarketingLayout").then(m => ({ default: m.MarketingLayout })));
const MarketingPage = lazyWithRecovery(() => import("./pages/marketing/MarketingPage"));
const BlogPage = lazyWithRecovery(() => import("./pages/marketing/BlogPage"));
const BlogPostPage = lazyWithRecovery(() => import("./pages/marketing/BlogPostPage"));
const MerchantWidgetPage = lazyWithRecovery(() => import("./pages/marketing/MerchantWidgetPage"));
const TrendInsightsPage = lazyWithRecovery(() => import("./pages/marketing/TrendInsightsPage"));
const SafariExtensionInstallPage = lazyWithRecovery(() => import("./pages/marketing/SafariExtensionInstallPage"));
const ExtensionTutorialPage = lazyWithRecovery(() => import("./pages/marketing/ExtensionTutorialPage"));
const SafariExtensionDemoPage = lazyWithRecovery(() => import("./pages/marketing/SafariExtensionDemoPage"));
const UnderTheHoodPage = lazyWithRecovery(() => import("./pages/marketing/UnderTheHoodPage"));
const AboutMarketingPage = lazyWithRecovery(() => import("./pages/marketing/AboutMarketingPage"));
const RoadmapPage = lazyWithRecovery(() => import("./pages/marketing/RoadmapPage"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <img src={wLogoIcon} alt="Loading" className="h-10 w-10 animate-spin" />
  </div>
);

/**
 * React Query client instance.
 */
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      import('@/services/errorTracking').then(({ captureException }) => {
        captureException(error, {
          source: 'query',
          queryKey: JSON.stringify(query.queryKey),
        });
      });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2, // 2 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes
      retry: 1,
    },
    mutations: {
      retry: 0, // Don't retry mutations by default
      onError: (error) => {
        import('@/services/errorTracking').then(({ captureException }) => {
          captureException(error, { source: 'mutation' });
        });
      },
    },
  },
});

/**
 * Root Application Component
 *
 * Provider hierarchy (outer to inner):
 * 1. QueryClientProvider - React Query for data fetching
 * 2. DevModeProvider - Development mode toggle
 * 3. TooltipProvider - Global tooltip context
 *
 * Global components:
 * - Toaster/Sonner - Toast notifications
 * - ScrollToTop - Reset scroll on navigation
 * - ReferralHandler - Process referral codes from URL
 *
 * Routes are split into groups:
 * - Marketing routes: / (root) - public marketing site
 * - App routes: /app/* - main app with header + bottom nav
 * - Admin routes: /admin/* - admin panel
 * - Standalone routes: Full-page views without shared layout
 */
const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
        <AuthProvider>
          <DeepLinkProvider>
            <ImpersonationProvider>
              <ImpersonationBanner />
            <ThemeSyncProvider>
                <DevModeProvider>
                  <VideoMuteProvider>
                  <TooltipProvider>
                    <Toaster />
                    <OtaUpdateNotifier />
                    <BrowserRouter>
                      <BadgeCelebrationOverlay />
                      <PhoneFrame>
                      <NewUserOnboardingProvider>
                      <EventLoggerProvider>
                        <CapacitorProvider>
                          <GlobalErrorCapture />
                          <ExchangeRatesPrefetch />
                          <ScrollToTop />
                          <AttributionHandler />
                          <Routes>
                    {/* Marketing site - at root, redirects to /app on native */}
                    <Route
                      path="/"
                      element={
                        Capacitor.isNativePlatform() ? (
                          <Navigate to="/app" replace />
                        ) : (
                          <RouteErrorBoundary>
                            <Suspense fallback={<PageLoader />}>
                              <MarketingLayout />
                            </Suspense>
                          </RouteErrorBoundary>
                        )
                      }
                    >
                      <Route index element={<Suspense fallback={<PageLoader />}><MarketingPage /></Suspense>} />
                      <Route path="blog" element={<Suspense fallback={<PageLoader />}><BlogPage /></Suspense>} />
                      <Route path="blog/:slug" element={<Suspense fallback={<PageLoader />}><BlogPostPage /></Suspense>} />
                      <Route path="merchant-widget" element={<Suspense fallback={<PageLoader />}><MerchantWidgetPage /></Suspense>} />
                      <Route path="trend-insights" element={<Suspense fallback={<PageLoader />}><TrendInsightsPage /></Suspense>} />
                      <Route path="safari-extension" element={<Suspense fallback={<PageLoader />}><SafariExtensionInstallPage /></Suspense>} />
                      <Route path="extension-tutorial" element={<Suspense fallback={<PageLoader />}><ExtensionTutorialPage /></Suspense>} />
                      <Route path="safari-extension/demo" element={<Suspense fallback={<PageLoader />}><SafariExtensionDemoPage /></Suspense>} />
                      <Route path="under-the-hood" element={<Suspense fallback={<PageLoader />}><UnderTheHoodPage /></Suspense>} />
                      <Route path="about" element={<Suspense fallback={<PageLoader />}><AboutMarketingPage /></Suspense>} />
                      <Route path="roadmap" element={<Suspense fallback={<PageLoader />}><RoadmapPage /></Suspense>} />
                    </Route>
                    {/* Backwards compat: old /site/* → root marketing */}
                    <Route path="/site" element={<Navigate to="/" replace />} />
                    <Route path="/site/*" element={<Navigate to="/" replace />} />
                    {/* Public shared collection - no auth required */}
                    <Route path="/shared/:token" element={<Suspense fallback={<PageLoader />}><SharedCollectionPage /></Suspense>} />
                    <Route path="/g/:token" element={<Suspense fallback={<PageLoader />}><SharedCollectionPage /></Suspense>} />
                    {/* OAuth native bridge - redirects from Safari back to native app */}
                    <Route path="/auth/native-callback" element={<Suspense fallback={<PageLoader />}><NativeAuthCallbackPage /></Suspense>} />
                    {/* Main app routes - all under /app, gated by access config */}
                    <Route path="/app/onboarding" element={<AppAccessGate><Navigate to="/app/welcome" replace /></AppAccessGate>} />
                    <Route path="/app/welcome" element={<AppAccessGate><Suspense fallback={<PageLoader />}><WelcomePage /></Suspense></AppAccessGate>} />
                    {/* Tab routes - handled by TabView component (keeps all tabs mounted) */}
                    <Route path="/app" element={<AppAccessGate><AppLayout /></AppAccessGate>} />
                    <Route path="/app/saves" element={<Navigate to="/app" replace />} />
                    <Route path="/app/home" element={<Navigate to="/app" replace />} />
                    <Route path="/app/try-on" element={<AppAccessGate><AppLayout /></AppAccessGate>} />
                    {/* Studio routes */}
                    <Route path="/app/studio" element={<AppAccessGate><AppLayout /></AppAccessGate>} />
                    <Route path="/app/studio/hairdresser" element={<AppAccessGate><AppLayout /></AppAccessGate>} />
                    <Route path="/app/studio/profile" element={<AppAccessGate><AppLayout /></AppAccessGate>} />
                    <Route path="/app/studio/celebrity" element={<AppAccessGate><AppLayout /></AppAccessGate>} />
                    <Route path="/app/studio/body-gym" element={<AppAccessGate><AppLayout /></AppAccessGate>} />
                    <Route path="/app/studio/my-looks" element={<AppAccessGate><AppLayout /></AppAccessGate>} />
                    {/* Redirect old Style Studio routes to new Studio tab */}
                    <Route path="/app/style-profile" element={<Navigate to="/app/studio" replace />} />
                    <Route path="/app/celebrity-match" element={<Navigate to="/app/studio/celebrity" replace />} />
                    <Route path="/app/body-gym" element={<Navigate to="/app/studio/body-gym" replace />} />
                    <Route path="/app/vote" element={<AppAccessGate><AppLayout /></AppAccessGate>} />
                    <Route path="/app/feed" element={<Navigate to="/app/vote" replace />} />
                    <Route path="/app/discover" element={<Navigate to="/app/vote" replace />} />
                    <Route path="/app/explore" element={<Navigate to="/app/vote" replace />} />
                    <Route path="/app/chat" element={<AppAccessGate><AppLayout /></AppAccessGate>} />
                    <Route element={<AppAccessGate><AnimatedOutlet /></AppAccessGate>}>
                      <Route path="/app/notifications" element={<RouteErrorBoundary><Suspense fallback={<PageLoader />}><NotificationsPage /></Suspense></RouteErrorBoundary>} />
                      <Route path="/app/settings" element={<Suspense fallback={<PageLoader />}><SettingsPage /></Suspense>} />
                      <Route path="/app/settings/account" element={<Suspense fallback={<PageLoader />}><AccountSecurityPage /></Suspense>} />
                      <Route path="/app/settings/notifications" element={<Suspense fallback={<PageLoader />}><NotificationSettingsPage /></Suspense>} />
                      <Route path="/app/settings/region" element={<Suspense fallback={<PageLoader />}><RegionCurrencyPage /></Suspense>} />
                      <Route path="/app/settings/profile-style" element={<Suspense fallback={<PageLoader />}><ProfileStylePage /></Suspense>} />
                      <Route path="/app/reset-password" element={<Suspense fallback={<PageLoader />}><ResetPasswordPage /></Suspense>} />
                      <Route path="/app/profile" element={<Suspense fallback={<PageLoader />}><ProfilePage /></Suspense>} />
                      <Route path="/app/user/:userId" element={<Suspense fallback={<PageLoader />}><UserProfilePage /></Suspense>} />
                      <Route path="/app/product/:id" element={<Navigate to="/app" replace />} />
                      <Route path="/app/saved/:id" element={<Suspense fallback={<PageLoader />}><SavedItemDetailPage /></Suspense>} />
                      <Route path="/app/collection/:id" element={<Suspense fallback={<PageLoader />}><CollectionDetailPage /></Suspense>} />
                      <Route path="/app/safari-extension" element={<Suspense fallback={<PageLoader />}><SafariExtensionPage /></Suspense>} />
                      <Route path="/app/chrome-extension" element={<Suspense fallback={<PageLoader />}><ChromeExtensionPage /></Suspense>} />
                      <Route path="/app/clean-out" element={<Suspense fallback={<PageLoader />}><CleanOutPage /></Suspense>} />
                      <Route path="/app/purchases" element={<Suspense fallback={<PageLoader />}><PurchasesPage /></Suspense>} />
                      <Route path="/app/price-alerts" element={<Suspense fallback={<PageLoader />}><PriceAlertsPage /></Suspense>} />
                      <Route path="/app/pricing" element={<Suspense fallback={<PageLoader />}><PricingPage /></Suspense>} />
                      
                      <Route path="/app/whats-new" element={<Suspense fallback={<PageLoader />}><WhatsNewPage /></Suspense>} />
                      <Route path="/app/friends" element={<Suspense fallback={<PageLoader />}><FriendsPage /></Suspense>} />
                      <Route path="/app/for-you" element={<Suspense fallback={<PageLoader />}><ForYouPage /></Suspense>} />
                      <Route path="/app/voice" element={<Suspense fallback={<PageLoader />}><VoiceAgentPage /></Suspense>} />
                      <Route path="/app/make-it-yours" element={<Suspense fallback={<PageLoader />}><MakeItYoursPage /></Suspense>} />
                      <Route path="/app/privacy" element={<Suspense fallback={<PageLoader />}><PrivacyPolicyPage /></Suspense>} />
                      <Route path="/app/price-guess" element={<Suspense fallback={<PageLoader />}><PriceGuessPage /></Suspense>} />
                      <Route path="/app/daily-outfit" element={<Suspense fallback={<PageLoader />}><DailyOutfitPage /></Suspense>} />
                      <Route path="/app/style-interview" element={<Suspense fallback={<PageLoader />}><StyleInterviewPage /></Suspense>} />
                      <Route path="/app/live-tryon" element={<Suspense fallback={<PageLoader />}><LiveTryOnPage /></Suspense>} />
                      <Route path="/app/style-persona" element={<Suspense fallback={<PageLoader />}><StylePersonaPage /></Suspense>} />
                      <Route path="/app/about" element={<Suspense fallback={<PageLoader />}><AboutPage /></Suspense>} />
                      <Route path="/app/style-analysis" element={<Navigate to="/app/studio" replace />} />
                      <Route path="/app/body-shape-analysis" element={<Navigate to="/app/studio" replace />} />
                      <Route path="/app/portrait-studio" element={<Navigate to="/app" replace />} />
                    </Route>
                    {/* Backwards compat: old app routes without /app prefix → redirect */}
                    <Route path="/onboarding" element={<Navigate to="/app/welcome" replace />} />
                    <Route path="/welcome" element={<Navigate to="/app/welcome" replace />} />
                    <Route path="/saves" element={<Navigate to="/app" replace />} />
                    <Route path="/home" element={<Navigate to="/app" replace />} />
                    <Route path="/try-on" element={<Navigate to="/app/try-on" replace />} />
                    <Route path="/vote" element={<Navigate to="/app/vote" replace />} />
                    <Route path="/feed" element={<Navigate to="/app/vote" replace />} />
                    <Route path="/discover" element={<Navigate to="/app/vote" replace />} />
                    <Route path="/explore" element={<Navigate to="/app/vote" replace />} />
                    <Route path="/chat" element={<Navigate to="/app/chat" replace />} />
                    <Route path="/studio" element={<Navigate to="/app/studio" replace />} />
                    <Route path="/studio/*" element={<Navigate to="/app/studio" replace />} />
                    <Route path="/notifications" element={<Navigate to="/app/notifications" replace />} />
                    <Route path="/settings" element={<Navigate to="/app/settings" replace />} />
                    <Route path="/settings/*" element={<Navigate to="/app/settings" replace />} />
                    <Route path="/profile" element={<Navigate to="/app/profile" replace />} />
                    <Route path="/user/:userId" element={<Navigate to="/app" replace />} />
                    <Route path="/saved/:id" element={<Navigate to="/app" replace />} />
                    <Route path="/collection/:id" element={<Navigate to="/app" replace />} />
                    <Route path="/safari-extension" element={<Navigate to="/app/safari-extension" replace />} />
                    <Route path="/chrome-extension" element={<Navigate to="/app/chrome-extension" replace />} />
                    <Route path="/clean-out" element={<Navigate to="/app/clean-out" replace />} />
                    <Route path="/purchases" element={<Navigate to="/app/purchases" replace />} />
                    <Route path="/price-alerts" element={<Navigate to="/app/price-alerts" replace />} />
                    <Route path="/pricing" element={<Navigate to="/app/pricing" replace />} />
                    <Route path="/whats-new" element={<Navigate to="/app/whats-new" replace />} />
                    <Route path="/friends" element={<Navigate to="/app/friends" replace />} />
                    <Route path="/for-you" element={<Navigate to="/app/for-you" replace />} />
                    <Route path="/voice" element={<Navigate to="/app/voice" replace />} />
                    <Route path="/make-it-yours" element={<Navigate to="/app/make-it-yours" replace />} />
                    <Route path="/privacy" element={<Navigate to="/app/privacy" replace />} />
                    <Route path="/price-guess" element={<Navigate to="/app/price-guess" replace />} />
                    <Route path="/daily-outfit" element={<Navigate to="/app/daily-outfit" replace />} />
                    <Route path="/style-interview" element={<Navigate to="/app/style-interview" replace />} />
                    <Route path="/live-tryon" element={<Navigate to="/app/live-tryon" replace />} />
                    <Route path="/style-persona" element={<Navigate to="/app/style-persona" replace />} />
                    <Route path="/about" element={<Navigate to="/app/about" replace />} />
                    <Route path="/reset-password" element={<Navigate to="/app/reset-password" replace />} />
                    <Route path="/style-analysis" element={<Navigate to="/app/studio" replace />} />
                    <Route path="/body-shape-analysis" element={<Navigate to="/app/studio" replace />} />
                    <Route path="/portrait-studio" element={<Navigate to="/app" replace />} />
                    <Route path="/style-profile" element={<Navigate to="/app/studio" replace />} />
                    <Route path="/celebrity-match" element={<Navigate to="/app/studio/celebrity" replace />} />
                    <Route path="/body-gym" element={<Navigate to="/app/studio/body-gym" replace />} />
                    <Route path="/product/:id" element={<Navigate to="/app" replace />} />
                    {/* Admin routes - lazy loaded and protected */}
                    <Route
                      path="/admin"
                      element={
                        <AdminGuard>
                          <RouteErrorBoundary>
                            <Suspense fallback={<PageLoader />}>
                              <AdminLayout />
                            </Suspense>
                          </RouteErrorBoundary>
                        </AdminGuard>
                      }
                    >
                      <Route index element={<Suspense fallback={<PageLoader />}><AdminDashboard /></Suspense>} />
                      <Route path="users" element={<Suspense fallback={<PageLoader />}><AdminUsers /></Suspense>} />
                      <Route path="user-personas" element={<Suspense fallback={<PageLoader />}><AdminUserPersonas /></Suspense>} />
                      <Route path="user-results" element={<Suspense fallback={<PageLoader />}><AdminUserResults /></Suspense>} />
                      <Route path="analytics" element={<Suspense fallback={<PageLoader />}><AdminAnalytics /></Suspense>} />
                      <Route path="cohorts" element={<Suspense fallback={<PageLoader />}><AdminCohorts /></Suspense>} />
                      <Route path="features" element={<Suspense fallback={<PageLoader />}><AdminFeatureAdoption /></Suspense>} />
                      <Route path="growth" element={<Suspense fallback={<PageLoader />}><AdminGrowth /></Suspense>} />
                      
                      <Route path="saved-items" element={<Suspense fallback={<PageLoader />}><AdminSavedItems /></Suspense>} />
                      <Route path="reports" element={<Suspense fallback={<PageLoader />}><AdminReports /></Suspense>} />
                      <Route path="messaging" element={<Suspense fallback={<PageLoader />}><AdminMessaging /></Suspense>} />
                      <Route path="promo-cards" element={<Suspense fallback={<PageLoader />}><AdminPromoCards /></Suspense>} />
                      
                      <Route path="quick-try" element={<Suspense fallback={<PageLoader />}><AdminQuickTry /></Suspense>} />
                      <Route path="ai-prompts" element={<Suspense fallback={<PageLoader />}><AdminAIPrompts /></Suspense>} />
                      <Route path="api-usage" element={<Suspense fallback={<PageLoader />}><AdminAPIUsage /></Suspense>} />
                      <Route path="sponsored-polls" element={<Suspense fallback={<PageLoader />}><AdminSponsoredPolls /></Suspense>} />
                      <Route path="referrals" element={<Suspense fallback={<PageLoader />}><AdminReferrals /></Suspense>} />
                      <Route path="feedback" element={<Suspense fallback={<PageLoader />}><AdminFeedback /></Suspense>} />
                      <Route path="whats-new" element={<Suspense fallback={<PageLoader />}><AdminWhatsNew /></Suspense>} />
                      <Route path="events" element={<Suspense fallback={<PageLoader />}><AdminEvents /></Suspense>} />
                      <Route path="generations" element={<Suspense fallback={<PageLoader />}><AdminGenerations /></Suspense>} />
                      <Route path="social-proof" element={<Suspense fallback={<PageLoader />}><AdminSocialProof /></Suspense>} />
                      <Route path="roadmap-polls" element={<Suspense fallback={<PageLoader />}><AdminRoadmapPolls /></Suspense>} />
                      <Route path="app-reviews" element={<Suspense fallback={<PageLoader />}><AdminAppReviews /></Suspense>} />
                      <Route path="blog" element={<Suspense fallback={<PageLoader />}><AdminBlog /></Suspense>} />
                      <Route path="error-intelligence" element={<Suspense fallback={<PageLoader />}><AdminErrorIntelligence /></Suspense>} />
                      <Route path="feature-flags" element={<Suspense fallback={<PageLoader />}><AdminFeatureFlags /></Suspense>} />
                      <Route path="item-issues" element={<Suspense fallback={<PageLoader />}><AdminItemIssues /></Suspense>} />
                      <Route path="share-texts" element={<Suspense fallback={<PageLoader />}><AdminShareTexts /></Suspense>} />
                      <Route path="app-access" element={<Suspense fallback={<PageLoader />}><AdminAppAccess /></Suspense>} />
                      
                    </Route>
                    <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFound /></Suspense>} />
                          </Routes>
                        </CapacitorProvider>
                      </EventLoggerProvider>
                      </NewUserOnboardingProvider>
                      </PhoneFrame>
                    </BrowserRouter>
                  </TooltipProvider>
                  </VideoMuteProvider>
                </DevModeProvider>
            </ThemeSyncProvider>
          </ImpersonationProvider>
          </DeepLinkProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
